/**
 * BomTreeSimulate — "simulate this order against current stock" mode.
 *
 * Lets the operator pick a manufactured product + quantity, then walks the
 * BOM via POST /api/manufacturing/simulate and renders the tree with
 * green/yellow/red status colouring per node:
 *
 *   green   — every leaf under this node has stock to cover demand
 *   yellow  — this subtree is green, but a sibling subtree under the same
 *             parent is not (parallel work, ships when slowest sibling done)
 *   red     — at least one leaf under this node is short
 *
 * Click a red node to open the side panel with shortfall rows (required /
 * available / shortfall per leaf).
 *
 * Pulse animation: the path from the root to every red leaf gets a
 * 600ms CSS animation (respects prefers-reduced-motion). Implementation
 * detail: we tag pulsing edges via a className and rely on a single
 * <style> block scoped to this component.
 *
 * Previously lived in SimulationFlow.tsx; pan/zoom/expand/layout machinery
 * now lives in the shared BomTreeCore.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  ManufacturingProduct,
  PartsByCodeResponse,
  SimulationNode,
} from '../../types/manufacturing';
import { useTranslation } from '../../i18n';
import { useManufacturingSimulation } from './Manufacturing.mutations';
import {
  BomTreeCore,
  BomTreeCoreHandle,
  BomTreeToolbar,
  type BomTreeRenderEdgeArgs,
  type BomTreeRenderNodeArgs,
} from './BomTreeCore';

interface Props {
  products: ManufacturingProduct[];
  // Reserved: same lookup BomTreeExplore uses for the (i)/PDF chips. Not
  // wired into Simulate-mode NodeRect yet (the green/yellow/red status is
  // the primary signal there) but accepted so the parent can pass a single
  // shape and a future iteration can surface drawing links inline.
  partsByCode?: PartsByCodeResponse;
  stockSyncedAtFallback?: string | null;
}

const NODE_W = 260;
const NODE_H = 64;

// Hoisted to module scope so the BomTreeCore dims memo doesn't invalidate
// on every parent rerender (would otherwise trigger the dims `useMemo`
// recompute path inside the core).
const SIMULATE_DIMS = { nodeW: NODE_W, nodeH: NODE_H, xGap: 80, yGap: 16 };

// Status palette per spec.
const STATUS_COLORS: Record<string, string> = {
  green: '#16a34a',
  yellow: '#eab308',
  red: '#dc2626',
};

// Build the set of (parentId-childId) edge keys that should pulse: every
// edge on a path from the root through any non-green node. Both red and
// yellow nodes count as "non-green" — red is the bottleneck, yellow is
// the parallel work waiting on it. Returns whether this subtree contains
// any non-green node so the caller can decide to pulse the edge into it.
function collectPulsingEdges(node: SimulationNode, out: Set<string>): boolean {
  let hasNonGreenDescendant = node.status !== 'green';
  for (const c of node.children) {
    const childNonGreen = collectPulsingEdges(c, out);
    if (childNonGreen) {
      out.add(`${node.id}->${c.id}`);
      hasNonGreenDescendant = true;
    }
  }
  return hasNonGreenDescendant;
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const ageMs = Date.now() - ts;
  const ageHours = ageMs / (1000 * 60 * 60);
  if (ageHours < 1) return `${Math.round(ageMs / 60000)}m ago`;
  if (ageHours < 48) return `${Math.round(ageHours)}h ago`;
  return `${Math.round(ageHours / 24)}d ago`;
}

interface NodeRectProps {
  args: BomTreeRenderNodeArgs<SimulationNode>;
  onSelect: (node: SimulationNode) => void;
}

function NodeRect({ args, onSelect }: NodeRectProps) {
  const { node, pos, isExpanded, hasChildren, onToggle } = args;
  const color = STATUS_COLORS[node.status] ?? STATUS_COLORS.green;
  const label = node.code ?? node.name.slice(0, 26);
  const fullName = node.name;
  const nx = pos.x;
  const ny = pos.y;

  return (
    <g
      data-testid={`manufacturing-simulation-node-${node.id}`}
      style={{ cursor: node.status === 'red' ? 'pointer' : 'default' }}
      onClick={() => {
        if (node.status === 'red') onSelect(node);
      }}
    >
      <rect
        x={nx}
        y={ny}
        width={NODE_W}
        height={NODE_H}
        rx={8}
        fill="#ffffff"
        stroke={color}
        strokeWidth={2}
      />
      <rect x={nx} y={ny} width={6} height={NODE_H} rx={3} fill={color} />

      <text x={nx + 14} y={ny + 18} fontSize={13} fontWeight={700} fill="#0f172a">
        {label.length > 26 ? `${label.slice(0, 26)}…` : label}
      </text>
      {fullName !== label && (
        <text x={nx + 14} y={ny + 33} fontSize={10} fill="#475569">
          {fullName.length > 36 ? `${fullName.slice(0, 36)}…` : fullName}
        </text>
      )}
      <text x={nx + 14} y={ny + 51} fontSize={11} fill="#64748b">
        need {node.qty_required.toLocaleString()}
        {node.is_leaf && node.available !== null && <> • have {node.available.toLocaleString()}</>}
        {node.is_phantom && <> • phantom</>}
      </text>

      {hasChildren && (
        <g
          transform={`translate(${nx + NODE_W - 28}, ${ny + 8})`}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <circle cx={11} cy={11} r={11} fill={color} fillOpacity={0.15} stroke={color} />
          {isExpanded ? (
            <path d="M 6 13 L 11 8 L 16 13" fill="none" stroke={color} strokeWidth={2} />
          ) : (
            <path d="M 6 9 L 11 14 L 16 9" fill="none" stroke={color} strokeWidth={2} />
          )}
        </g>
      )}
    </g>
  );
}

export function BomTreeSimulate({
  products,
  stockSyncedAtFallback,
  partsByCode: _partsByCode,
}: Props) {
  const manufacturedProducts = useMemo(
    () =>
      products
        .filter((p) => p.is_manufactured)
        .sort((a, b) => (a.default_code ?? a.name).localeCompare(b.default_code ?? b.name)),
    [products]
  );

  const { t } = useTranslation();
  const [productId, setProductId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedNode, setSelectedNode] = useState<SimulationNode | null>(null);

  const simulation = useManufacturingSimulation();
  const report = simulation.data ?? null;
  const loading = simulation.isPending;

  // Clear side-panel selection whenever a fresh sim lands.
  useEffect(() => {
    if (report?.root) setSelectedNode(null);
  }, [report]);

  const handleSimulate = () => {
    if (!productId || quantity <= 0) return;
    simulation.mutate({ product_id: productId, quantity });
  };

  // Pulsing edges are static per simulation; collected once per result.
  const pulsingEdges = useMemo(() => {
    if (!report) return new Set<string>();
    const out = new Set<string>();
    collectPulsingEdges(report.root, out);
    return out;
  }, [report]);

  const handleRef = useRef<BomTreeCoreHandle>(null);

  const stockSyncedAt = report?.stock_synced_at ?? stockSyncedAtFallback ?? null;

  // Stable references for the BomTreeCore render-props API. Without these,
  // every parent re-render hands BomTreeCoreInner fresh function identities
  // and invalidates its allKeys / layout memos — a full tree walk per
  // page rerender. See PR review on #749.
  const childrenOf = useCallback((n: SimulationNode) => n.children, []);
  const keyOf = useCallback((n: SimulationNode) => n.id, []);
  const renderNode = useCallback(
    (args: BomTreeRenderNodeArgs<SimulationNode>) => (
      <NodeRect args={args} onSelect={(sel) => setSelectedNode(sel)} />
    ),
    []
  );
  const renderEdge = useCallback(
    (args: BomTreeRenderEdgeArgs<SimulationNode>) => {
      const { parent, child, edgePath } = args;
      const edgeKey = `${parent.id}->${child.id}`;
      const pulse = pulsingEdges.has(edgeKey);
      // Pulsing edges adopt the child's status colour so red and yellow
      // critical paths stay visually distinct. Non-pulsing edges stay
      // muted slate so they read as "fine, don't look here."
      const stroke = pulse ? STATUS_COLORS[child.status] ?? STATUS_COLORS.red : '#94a3b8';
      return (
        <path
          key={`edge-${edgeKey}`}
          d={edgePath}
          fill="none"
          stroke={stroke}
          strokeWidth={pulse ? 2.5 : 1.5}
          strokeOpacity={pulse ? 0.95 : 0.7}
          className={pulse ? 'sim-edge-pulse' : undefined}
        />
      );
    },
    [pulsingEdges]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="manufacturing-simulation">
      {/* Scoped pulse keyframes, gated on prefers-reduced-motion. */}
      <style>{`
        @keyframes sim-pulse {
          0%, 100% { stroke-opacity: 0.4; }
          50% { stroke-opacity: 1; }
        }
        .sim-edge-pulse {
          animation: sim-pulse 600ms ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .sim-edge-pulse { animation: none; }
        }
      `}</style>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexShrink: 0 }}>
          Simulate order
        </Typography>
        <Select
          size="small"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          displayEmpty
          sx={{ minWidth: 320 }}
          inputProps={{ 'data-testid': 'manufacturing-simulation-product' }}
        >
          <MenuItem value="" disabled>
            <em>Pick a manufactured product…</em>
          </MenuItem>
          {manufacturedProducts.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.default_code ? `${p.default_code} — ${p.name}` : p.name}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          type="number"
          label="Quantity"
          value={quantity}
          inputProps={{ min: 1, max: 10000, 'data-testid': 'manufacturing-simulation-qty' }}
          onChange={(e) => {
            const next = Number(e.target.value);
            setQuantity(Number.isFinite(next) && next > 0 ? next : 1);
          }}
          sx={{ width: 120 }}
        />
        <Button
          variant="contained"
          onClick={handleSimulate}
          disabled={!productId || quantity <= 0 || loading}
          data-testid="manufacturing-simulation-run"
        >
          {loading ? 'Simulating…' : 'Simulate'}
        </Button>
      </Stack>

      {simulation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('manufacturing.failedToSimulate')}
        </Alert>
      )}

      {report && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
            <Chip
              size="small"
              label={`Status: ${report.root.status.toUpperCase()}`}
              sx={{
                bgcolor: STATUS_COLORS[report.root.status],
                color: '#ffffff',
                fontWeight: 700,
              }}
            />
            <Chip
              size="small"
              label={`Missing parts: ${report.total_missing_parts}`}
              variant="outlined"
              color={report.total_missing_parts > 0 ? 'error' : 'success'}
            />
            {report.truncated_at && (
              <Chip
                size="small"
                label="Tree truncated at depth cap"
                variant="outlined"
                color="warning"
              />
            )}
            <Box sx={{ flex: 1 }} />
            <BomTreeToolbar handleRef={handleRef} disabled={!report} />
          </Stack>

          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            Stock as of{' '}
            {stockSyncedAt
              ? `${formatRelative(stockSyncedAt)} (${new Date(stockSyncedAt).toLocaleString()})`
              : 'never — run the manufacturing snapshot'}
            . Click any red node for shortfall details.
          </Typography>

          <BomTreeCore<SimulationNode>
            ref={handleRef}
            root={report.root}
            childrenOf={childrenOf}
            keyOf={keyOf}
            renderNode={renderNode}
            renderEdge={renderEdge}
            dims={SIMULATE_DIMS}
          />
        </>
      )}

      {!report && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Pick a manufactured product, set a quantity, and hit Simulate to see which parts are short
          for that order.
        </Typography>
      )}

      <Drawer
        anchor="right"
        open={selectedNode !== null}
        onClose={() => setSelectedNode(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Missing parts</Typography>
            <IconButton onClick={() => setSelectedNode(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {selectedNode && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {selectedNode.code ?? '(no code)'} — {selectedNode.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedNode.missing_parts.length} shortfall row
                {selectedNode.missing_parts.length === 1 ? '' : 's'} under this node.
              </Typography>
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Part</TableCell>
                    <TableCell align="right">Need</TableCell>
                    <TableCell align="right">Have</TableCell>
                    <TableCell align="right">Short</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedNode.missing_parts.map((mp, idx) => (
                    <TableRow key={`${mp.code ?? 'no-code'}-${mp.name}-${idx}`}>
                      <TableCell>{mp.code ?? '—'}</TableCell>
                      <TableCell>{mp.name}</TableCell>
                      <TableCell align="right">{mp.required.toLocaleString()}</TableCell>
                      <TableCell align="right">{mp.available.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: STATUS_COLORS.red, fontWeight: 700 }}>
                        {mp.shortfall.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Box>
      </Drawer>
    </Paper>
  );
}
